import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../config";
import CustomerLogin from "./CustomerLogin";
import BackButton from "./BackButton";
import NotificationCart from "./NotificationCart";
import CartIcon from "./Carticon";

const MenuDisplay = () => {
  const query = new URLSearchParams(useLocation().search);
  const token = query.get("token");

  const [menu, setMenu] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("currentCart");
    const cartTimestamp = localStorage.getItem("cartTimestamp");

    if (savedCart && cartTimestamp) {
      const isExpired = Date.now() - parseInt(cartTimestamp) > 60 * 60 * 1000;

      if (isExpired) {
        localStorage.removeItem("currentCart");
        localStorage.removeItem("cartTimestamp");
        return [];
      }
      return JSON.parse(savedCart);
    }
    return [];
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [restaurantDetail, setRestaurantDetail] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showRestaurantDetails, setShowRestaurantDetails] = useState(false);
  const [restaurant_id, setRestaurant_id] = useState(null);

  // Desktop event handlers
  const handleRestaurantDetailsHover = () => {
    setShowRestaurantDetails(true);
  };

  const handleRestaurantDetailsLeave = () => {
    setShowRestaurantDetails(false);
  };

  // Mobile event handler
  const handleRestaurantDetailsClick = () => {
    setShowRestaurantDetails((prevState) => !prevState);
  };

  // New states for login functionality
  const [isLoginVisible, setLoginVisible] = useState(false);
  const [userData, setUserData] = useState(() => {
    const storedData = localStorage.getItem("customerData");
    return storedData && storedData !== "undefined"
      ? JSON.parse(storedData)
      : null;
  });

  useEffect(() => {
    const handleScroll = () => {
      const headerHeight = 100;
      setIsScrolled(window.scrollY > headerHeight);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const showLoginButtonPaths = ["/menu-display", "/cart", "/customer_details"];
  // Login handlers
  const handleLoginClick = () => setLoginVisible(true);
  const handleCloseLogin = () => setLoginVisible(false);
  const handleLoginSuccess = (data) => {
    setUserData(data);
    setLoginVisible(false);
  };

  // Cart position states and handlers
  const [touching, setTouching] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [cartPosition, setCartPosition] = useState(() => {
    const saved = localStorage.getItem("cartPosition");
    return saved ? JSON.parse(saved) : { x: window.innerWidth - 100, y: 20 };
  });

  const [touchPosition, setTouchPosition] = useState(() => {
    // Better mobile positioning
    const isMobile = window.innerWidth <= 768;
    return {
      x: isMobile ? window.innerWidth - 60 : window.innerWidth - 80,
      y: isMobile ? window.innerHeight - 120 : window.innerHeight / 2,
    };
  });

  // Drag handlers
  const handleDragStart = (e) => {
    const rect = e.target.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    e.dataTransfer.setData("text/plain", `${offsetX},${offsetY}`);
  };

  const handleDrag = (e) => {
    if (e.clientX && e.clientY) {
      const [offsetX, offsetY] = e.dataTransfer
        .getData("text/plain")
        .split(",");
      const x = Math.min(
        Math.max(0, e.clientX - offsetX),
        window.innerWidth - 100
      );
      const y = Math.min(
        Math.max(0, e.clientY - offsetY),
        window.innerHeight - 100
      );
      setCartPosition({ x, y });
      localStorage.setItem("cartPosition", JSON.stringify({ x, y }));
    }
  };

  // Mobile touch handlers
  const handleMobileTouchStart = (e) => {
    const touch = e.touches[0];
    setTouching(true);
    setDragOffset({
      x: touch.clientX - touchPosition.x,
      y: touch.clientY - touchPosition.y,
    });
  };

  const handleMobileTouchMove = (e) => {
    if (touching) {
      const touch = e.touches[0];
      const newX = Math.min(
        Math.max(0, touch.clientX - dragOffset.x),
        window.innerWidth - 80
      );
      const newY = Math.min(
        Math.max(0, touch.clientY - dragOffset.y),
        window.innerHeight - 80
      );
      setTouchPosition({ x: newX, y: newY });
    }
  };

  const handleMobileTouchEnd = () => setTouching(false);
  // added as a function to clear the cart on restaurant change.
  function handleRestaurantSwitch(restaurant_id, setCart, showNotification) {
    const previousRestaurantId = localStorage.getItem("previous_restaurant_id");

    if (
      previousRestaurantId &&
      restaurant_id &&
      previousRestaurantId !== restaurant_id
    ) {
      console.log("Cart cleared due to restaurant switch.");
      setCart([]);
      localStorage.removeItem("currentCart");
      showNotification(
        "Your cart has been cleared as you switched to a different restaurant",
        "info"
      );
    }

    if (restaurant_id) {
      localStorage.setItem("previous_restaurant_id", restaurant_id);
      console.log("Stored Restaurant ID:", restaurant_id);
    }
  }

  // Image and cart handlers
  const handleImageClick = (imageUrl) => {
    if (imageUrl) {
      setPreviewImage(imageUrl);
    }
  };
  const closePreview = () => setPreviewImage(null);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/customer/display-menu/${token}`
        );
        const allItems = response.data;
        if (
          allItems.message &&
          allItems.message === "No menu items found for this restaurant"
        ) {
          setError("No menu items in the restaurant.");
        } else {
          setMenu(allItems.menu);
          setRestaurantDetail(allItems.restaurant);

          setRestaurant_id(allItems.restaurant.restaurant_id);
          const uniqueCategories = [
            ...new Set(
              allItems.menu
                .filter((item) => item.category !== "todaysSpecial")
                .map((item) => item.category)
            ),
          ];
          setCategories(uniqueCategories);
        }
      } catch (error) {
        // Check for subscription-related errors first
        if (
          error.response &&
          error.response.status === 403 &&
          error.response.data &&
          error.response.data.message ===
            "Subscription is not valid for this restaurant"
        ) {
          setError(
            "This restaurant's subscription has expired. Please contact the restaurant for assistance."
          );
        }
        // Check if the error response contains the specific message for no menu items
        else if (
          error.response &&
          error.response.data &&
          error.response.data.message ===
            "No menu items found for this restaurant"
        ) {
          setError("No menu items in the restaurant.");
        } else {
          setError("No menu items in the restaurant. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    if (cart.length > 0) {
      localStorage.setItem("currentCart", JSON.stringify(cart));
      localStorage.setItem("cartTimestamp", Date.now().toString());
    }
    fetchMenu();
  }, [token, restaurant_id, cart]);

  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");
  const [isNotification, setIsNotification] = useState(false);

  const showNotification = (message, status) => {
    setNotificationMessage(message);
    setNotificationStatus(status);
    setIsNotification(true);
    setTimeout(() => {
      setIsNotification(false);
    }, 9000);
  };

  // Cart management functions
  const handleAddToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((i) => i.menu_id === item.menu_id);
      return existingItem
        ? prevCart.map((i) =>
            i.menu_id === item.menu_id ? { ...i, quantity: i.quantity + 1 } : i
          )
        : [...prevCart, { ...item, quantity: 1 }];
    });
    localStorage.setItem("currentCart", JSON.stringify(cart));
  };

  const handleUpdateCart = (menuId, quantity) => {
    quantity <= 0
      ? setCart((prevCart) =>
          prevCart.filter((item) => item.menu_id !== menuId)
        )
      : setCart((prevCart) =>
          prevCart.map((item) =>
            item.menu_id === menuId ? { ...item, quantity } : item
          )
        );
    localStorage.setItem("currentCart", JSON.stringify(cart));
  };

  useEffect(() => {
    if (restaurantDetail) {
      localStorage.setItem(
        "current_restaurant_details",
        JSON.stringify(restaurantDetail)
      );
    }
  }, [restaurantDetail]);

  const handleCheckout = () => {
    if (cart.length === 0) {
      showNotification("Your cart is empty.", "error");
      return;
    }
    navigate("/cart", { state: { cart, restaurant_id } });
  };

  const calculateTotal = () =>
    cart
      .reduce((total, item) => total + item.price * item.quantity, 0)
      .toFixed(2);

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Loading menu...</p>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-6">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <p className="text-red-500 text-lg font-medium">{error}</p>
      </div>
    </div>
  );

  const specialItems = menu.filter((item) => item.category === "TodaysSpecial");
  const regularItems = menu.filter((item) => item.category !== "TodaysSpecial");

  return (
    <div className="max-w-7xl mx-auto p-4 md:mt-5">
      <BackButton />
      <div className="max-w-7xl mx-auto px-2 mt-18 md:mt-15">
        {isNotification && (
          <NotificationCart
            message={notificationMessage}
            status={notificationStatus}
          />
        )}
        <div className="max-w-6xl mx-auto relative">
          {/* Floating Header Card */}
          <div
            className={`${
              isScrolled ? "fixed top-0 shadow-lg" : "relative shadow-sm"
            } left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 transition-all duration-300`}
            onMouseEnter={handleRestaurantDetailsHover}
            onMouseLeave={handleRestaurantDetailsLeave}
            onClick={handleRestaurantDetailsClick}
          >
            <div className="max-w-7xl mx-auto p-4">
              <div className="flex flex-col gap-3">
                {/* Top row with restaurant name and login */}
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Restaurant Icon instead of logo */}
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900 truncate min-w-0">
                      {restaurantDetail.name}
                    </h1>
                  </div>

                  {showLoginButtonPaths.includes(location.pathname) && (
                    <div className="relative flex-shrink-0 ml-4">
                      {userData ? (
                        <div className="relative">
                          <button
                            onClick={() => setShowDropdown(!showDropdown)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                          >
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {userData.customer_name?.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-gray-700 truncate max-w-[100px] hidden sm:block">
                              {userData.customer_name}
                            </span>
                            <svg
                              className="w-4 h-4 text-gray-500 flex-shrink-0 hidden sm:block"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          {showDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                              <button
                                onClick={() => {
                                  navigate("/customer_orders", {
                                    state: { customerId: userData.customer_id },
                                  });
                                  setShowDropdown(false);
                                }}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                Order Status
                              </button>
                              <button
                                onClick={() => {
                                  localStorage.removeItem("customerData");
                                  setUserData(null);
                                  setShowDropdown(false);
                                }}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                Logout
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={handleLoginClick}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="hidden sm:block">Login</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Bottom row with address details */}
                <div
                  className={`text-sm text-gray-600 overflow-x-auto transition-all duration-300 ${
                    showRestaurantDetails ? "block opacity-100" : "hidden opacity-0"
                  }`}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="truncate">{restaurantDetail.address}</span>
                    </div>
                    <span className="text-gray-300">•</span>
                    <span className="truncate">{restaurantDetail.city}</span>
                    <span className="text-gray-300">•</span>
                    <span className="truncate">{restaurantDetail.state}</span>
                    {restaurantDetail.phone && (
                      <>
                        <span className="text-gray-300">•</span>
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>
                            {restaurantDetail.phone.replace(
                              /(\d{3})(\d{3})(\d{4})/,
                              "$1-$2-$3"
                            )}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Login Modal */}
        {isLoginVisible && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
              <CustomerLogin
                onClose={handleCloseLogin}
                onLoginSuccess={handleLoginSuccess}
              />
            </div>
          </div>
        )}

        {/* Cart Icon */}
        {cart.length > 0 && (
          <div className="fixed z-50">
            {" "}
            {/* Ensure high z-index */}
            <CartIcon
              cart={cart}
              setCart={setCart}
              showCartModal={showCartModal}
              setShowCartModal={setShowCartModal}
              touching={touching}
              touchPosition={touchPosition}
              handleMobileTouchStart={handleMobileTouchStart}
              handleMobileTouchMove={handleMobileTouchMove}
              handleMobileTouchEnd={handleMobileTouchEnd}
              cartPosition={cartPosition}
              handleDragStart={handleDragStart}
              handleDrag={handleDrag}
              handleUpdateCart={handleUpdateCart}
              handleAddToCart={handleAddToCart}
              calculateTotal={calculateTotal}
              handleCheckout={handleCheckout}
            />
          </div>
        )}

        {/* Today's Special Section */}
        {specialItems.length > 0 && (
          <div className="mb-12 relative">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full text-white">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
                <h2 className="text-xl font-bold">
                  Today's Special
                </h2>
              </div>
            </div>
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-6 pb-4 px-2">
                {specialItems.map((item) => (
                  <div
                    key={item.menu_id}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden flex-shrink-0 w-72 transform transition-all hover:scale-105 hover:shadow-xl"
                  >
                    <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden flex items-center justify-center relative">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.item_name}
                          className="w-full h-full object-cover cursor-pointer transition-transform hover:scale-110"
                          onClick={() => handleImageClick(item.image)}
                        />
                      ) : (
                        <div className="text-center p-4">
                          <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-gray-500 text-sm">No Image Available</span>
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        SPECIAL
                      </div>
                    </div>

                    <div className="p-5 flex-grow flex flex-col">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">
                        {item.item_name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-grow">
                        {item.description}
                      </p>

                      <div className="flex justify-between items-center">
                        {cart.find((i) => i.menu_id === item.menu_id) ? (
                          <div className="flex items-center space-x-3 bg-gray-100 rounded-lg px-3 py-2">
                            <button
                              className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                              onClick={() =>
                                handleUpdateCart(
                                  item.menu_id,
                                  (cart.find((i) => i.menu_id === item.menu_id)
                                    ?.quantity || 0) - 1
                                )
                              }
                            >
                              -
                            </button>
                            <span className="text-sm font-semibold min-w-8 text-center">
                              {
                                cart.find((i) => i.menu_id === item.menu_id)
                                  ?.quantity
                              }
                            </span>
                            <button
                              className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                              onClick={() => handleAddToCart(item)}
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleAddToCart(item)}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
                          >
                            Add to Cart
                          </button>
                        )}

                        <p className="text-blue-600 font-bold text-lg">
                          ${item.price}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="flex overflow-x-auto gap-3 mb-8 mt-6 pb-4 px-2 scrollbar-hide">
          <button
            className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap ${
              selectedCategory === "all"
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/25"
                : "bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
            onClick={() => setSelectedCategory("all")}
          >
            All Items
          </button>

          {categories
            .filter((category) => category !== "TodaysSpecial")
            .map((category) => (
              <button
                key={category}
                className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-500/25"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
        </div>

        {/* Regular Menu Items Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-20">
          {regularItems
            .filter(
              (item) =>
                selectedCategory === "all" || item.category === selectedCategory
            )
            .map((item) => (
              <div
                key={item.menu_id}
                className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-4 flex flex-col h-full border border-gray-100"
              >
                <div className="w-full h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden flex items-center justify-center mb-4">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.item_name}
                      className="w-full h-full object-cover cursor-pointer transition-transform hover:scale-105"
                      onClick={() => handleImageClick(item.image)}
                    />
                  ) : (
                    <div className="text-center p-4">
                      <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-gray-500 text-xs">No Image</span>
                    </div>
                  )}
                </div>

                <h3 className="font-bold text-gray-900 mb-2 line-clamp-1">
                  {item.item_name}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-grow">
                  {item.description}
                </p>

                <div className="flex items-center justify-between mt-auto">
                  {cart.find((i) => i.menu_id === item.menu_id) ? (
                    <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-2 py-1">
                      <button
                        className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                        onClick={() =>
                          handleUpdateCart(
                            item.menu_id,
                            (cart.find((i) => i.menu_id === item.menu_id)
                              ?.quantity || 0) - 1
                          )
                        }
                      >
                        -
                      </button>
                      <span className="text-sm font-semibold min-w-6 text-center">
                        {cart.find((i) => i.menu_id === item.menu_id)?.quantity}
                      </span>
                      <button
                        className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                        onClick={() => handleAddToCart(item)}
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleAddToCart(item)}
                      className="bg-blue-600 text-white px-4 py-2 text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
                    >
                      Add
                    </button>
                  )}

                  <span className="text-blue-600 font-bold text-base">
                    ${item.price}
                  </span>
                </div>
              </div>
            ))}
        </div>

        {/* Cart Total */}
        {cart.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl p-4 transform transition-transform">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div>
                <span className="text-xl font-bold text-gray-900">
                  Total: ${calculateTotal()}
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)} items in cart
                </p>
              </div>
              <button
                onClick={handleCheckout}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-lg font-semibold text-lg"
              >
                Checkout
              </button>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {previewImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
            onClick={closePreview}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={closePreview}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={previewImage}
                alt="Preview"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default MenuDisplay;